// A streamed Mac window, a widget and the Mac Windows picker, as pictures.
import { COLOR } from '../tokens.js'
import { INK, DIM, FAINT, ACCENT, MINT, GLASS, CONTROL_HI, SANS, MONO, box, fillBox, capsule, glass, clip, wrap, capsuleWidth, ago } from './paint.js'

export function macWindow(c, w, h) {
  const body = glass(c, w, h, { bar: true, radius: 30 })
  const inset = 14
  const innerW = w - inset * 2
  const innerH = body - inset * 2 - 44

  // the ornament: Home, the window's name, and the per-window controls (Move/Scroll, Mouse hover, Pointer
  // menu with aim, frame rate, trickle and Keep the app behind; Keys; Dictate) — and the Idle badge
  let ox = inset
  for (const [label, opts] of [['⌂', {}], ['Notes — Notes', { size: 13 }], ['Move', { size: 13 }], ['Mouse hover', { size: 13 }], ['Pointer ▾', { size: 13 }], ['Idle · 5 fps', { size: 13, fill: COLOR.controlOn, colour: COLOR.inkOnLight }], ['Dictate', { size: 13 }]]) {
    const cw = capsuleWidth(c, label, opts.size ?? 15) + 22
    if (ox + cw > w - inset) break
    capsule(c, ox, inset, cw, 30, label, { ...opts })
    ox += cw + 6
  }
  c.save()   // everything below sits under the ornament; the transform must not leak into the next redraw
  c.translate(0, 44)
  fillBox(c, inset, inset, innerW, innerH, 16, '#f6f6f7')

  // title bar
  c.save()
  box(c, inset, inset, innerW, innerH, 16); c.clip()
  c.fillStyle = '#e4e4e6'
  c.fillRect(inset, inset, innerW, 44)
  c.fillStyle = '#d0d0d4'
  c.fillRect(inset, inset + 43, innerW, 1)
  c.restore()

  ;['#ff5f57', '#febc2e', '#28c840'].forEach((colour, i) => {
    c.fillStyle = colour
    c.beginPath(); c.arc(inset + 26 + i * 21, inset + 22, 6.5, 0, Math.PI * 2); c.fill()
  })
  c.fillStyle = '#3a3a3e'
  c.font = `500 15px ${SANS}`
  c.textBaseline = 'middle'
  c.textAlign = 'center'
  c.fillText('HostModel.swift', inset + innerW / 2, inset + 22)
  c.textAlign = 'left'

  // gutter
  c.fillStyle = '#eeeef0'
  c.fillRect(inset, inset + 44, 52, innerH - 44)

  const CODE = [
    [['c', '/// Synchronous: also called when the app quits.']],
    [['k', 'func '], ['f', 'teardown'], ['p', '() {']],
    [['p', '    relay?.stop(); relay = nil']],
    [['k', '    for '], ['p', 'child '], ['k', 'in '], ['p', 'children '], ['k', 'where '], ['p', 'child.isRunning {']],
    [['p', '        child.terminate()']],
    [['p', '    }']],
    [['c', '    // Stop counting this workspace as awake.']],
    [['t', '    Awake'], ['p', '.shared.unwatch(accessLog: log)']],
    [['p', '}']],
  ]
  const COLOUR = { k: '#9b2393', f: '#3f6e74', t: '#4b21b0', c: '#5d6c79', p: '#1d1d20' }

  let line = inset + 72
  let number = 271
  c.save()
  box(c, inset, inset + 44, innerW, innerH - 44, 0); c.clip()   // a radius, or the path is NaN and clips everything away
  for (const parts of CODE) {
    c.fillStyle = '#a0a0a8'
    c.font = `400 13px ${MONO}`
    c.textAlign = 'right'
    c.fillText(String(number++), inset + 40, line)
    c.textAlign = 'left'

    let x = inset + 62
    c.font = `400 15px ${MONO}`
    for (const [kind, text] of parts) {
      c.fillStyle = COLOUR[kind] ?? COLOUR.p
      c.fillText(text, x, line)
      x += c.measureText(text).width
    }
    line += 23
  }
  c.restore()
  c.restore()   // the translate
}

export function widget(c, w, h) {
  glass(c, w, h, { radius: 38 })
  c.textBaseline = 'middle'

  c.fillStyle = MINT
  c.beginPath(); c.arc(38, 40, 7, 0, Math.PI * 2); c.fill()
  c.fillStyle = INK
  c.font = `600 17px ${SANS}`
  c.fillText('Open', 56, 41)
  c.fillStyle = DIM
  c.font = `400 17px ${SANS}`
  c.fillText('!482 · xr-harness', 116, 41)

  c.fillStyle = INK
  c.font = `600 22px ${SANS}`
  wrap(c, 'Drain the pipes in Tools.run', 34, 84, w - 68, 28, 2)

  const stages = [['build', MINT], ['test', MINT], ['sign', ACCENT], ['deploy', 'rgba(255,255,255,0.30)']]
  let x = 40
  stages.forEach(([name, colour], i) => {
    if (i < stages.length - 1) {
      c.strokeStyle = 'rgba(255,255,255,0.28)'
      c.lineWidth = 2.5
      c.beginPath(); c.moveTo(x + 10, 150); c.lineTo(x + 62, 150); c.stroke()
    }
    c.fillStyle = colour
    c.beginPath(); c.arc(x, 150, 9, 0, Math.PI * 2); c.fill()
    c.fillStyle = DIM
    c.font = `500 13px ${SANS}`
    c.textAlign = 'center'
    c.fillText(name, x, 176)
    c.textAlign = 'left'
    x += 72
  })

  c.fillStyle = FAINT
  c.font = `400 15px ${SANS}`
  c.fillText('2 approvals · 1 unresolved · 4m ago', 34, h - 34)
}

/// Mac Windows as a volume (MacAppsVolume.swift): Windows / Apps / Screen, icons hovering in a grid, a count on
/// an app with several windows, the Mac picker and the page.
export function macApps(c, w, h, live = null) {
  c.clearRect(0, 0, w, h)
  c.textBaseline = 'middle'
  if (live) return liveMacApps(c, w, h, live)
  // the strip on top: which Mac, Windows | Apps | Screen, the page
  fillBox(c, w / 2 - 250, 14, 500, 44, 22, GLASS)
  const menuW = capsuleWidth(c, 'Demo Mac ⌃⌄', 14) + 26
  capsule(c, w / 2 - 238, 20, menuW, 32, 'Demo Mac ⌃⌄', { size: 14 })
  const seg = ['Windows', 'Apps', 'Screen']
  let x = w / 2 - 60
  seg.forEach((label, i) => { capsule(c, x, 20, 80, 32, label, { fill: i === 1 ? CONTROL_HI : 'transparent', size: 14, weight: i === 1 ? 600 : 500 }); x += 84 })
  c.fillStyle = DIM; c.font = `400 13px ${MONO}`; c.fillText('1 / 2', w / 2 + 206, 36)
  // the icons, hovering: rounded squares with a glyph and the app's name under, a badge for several windows
  const apps = [['Xcode', '⌘', 2], ['Safari', '◎', 0], ['Terminal', '›_', 3], ['Finder', '☺', 0], ['Music', '♫', 0], ['Notes', '✎', 0], ['Mail', '✉', 1], ['Slack', '#', 0]]
  const cols = 4, size = 96, gap = 36
  const gridW = cols * size + (cols - 1) * gap
  apps.forEach(([name, glyph, count], i) => {
    const gx = (w - gridW) / 2 + (i % cols) * (size + gap), gy = 90 + Math.floor(i / cols) * (size + 58)
    fillBox(c, gx + 6, gy + 10, size, size, 22, 'rgba(0,0,0,0.18)')   // the shadow that says it floats
    fillBox(c, gx, gy, size, size, 22, ['#3b6df6', '#2fa6ff', '#2b2b2b', '#5aa9ff', '#ff4b5c', '#ffd24a', '#3d8bff', '#4a154b'][i])
    c.fillStyle = '#fff'; c.font = `600 40px ${SANS}`; c.textAlign = 'center'; c.fillText(glyph, gx + size / 2, gy + size / 2)
    if (count > 1) { fillBox(c, gx + size - 30, gy - 8, 30, 24, 12, 'rgba(255,255,255,0.9)'); c.fillStyle = '#111'; c.font = `700 13px ${SANS}`; c.fillText(String(count), gx + size - 15, gy + 4) }
    c.fillStyle = INK; c.font = `500 14px ${SANS}`; c.fillText(name, gx + size / 2, gy + size + 20)
    c.textAlign = 'left'
  })
  c.fillStyle = DIM; c.font = `400 13px ${SANS}`
  c.fillText('Tap an app: its window streams in; several windows — pick one; an app not running — it launches.', 24, h - 18)
}

/// The picker, live (MacAppsVolume.swift): the Mac's windows as tiles in a grid — the app's name large, the
/// window's title under it, a screen tile for each display — each tile's place kept in `live.macTiles` so a
/// tap lands on the right one.
function liveMacApps(c, w, h, live) {
  fillBox(c, w / 2 - 250, 14, 500, 44, 22, 'rgba(255,255,255,0.55)')
  c.fillStyle = '#1c2130'; c.font = `600 15px ${SANS}`; c.textAlign = 'center'
  c.fillText(clip(c, live.mac + ' — windows', 440), w / 2, 36)
  c.textAlign = 'left'
  const list = live.macWindows || []
  if (!list.length) { c.fillStyle = DIM; c.font = `400 15px ${SANS}`; c.textAlign = 'center'; c.fillText('The Mac lists no windows yet.', w / 2, h / 2); c.textAlign = 'left'; return }
  const cols = 3, size = 150, gap = 26
  const gridW = cols * size + (cols - 1) * gap
  const colours = ['#3b6df6', '#2fa6ff', '#5aa9ff', '#3d8bff', '#6c5ce7', '#00b894', '#e17055', '#fdcb6e']
  list.slice(0, 12).forEach((win, i) => {
    const gx = (w - gridW) / 2 + (i % cols) * (size + gap), gy = 84 + Math.floor(i / cols) * (size + 46)
    if (gy + size > h - 10) return
    fillBox(c, gx + 4, gy + 8, size, size * 0.62, 18, 'rgba(0,0,0,0.16)')
    const icon = win.bundleID && live.appIcons?.[win.bundleID]
    if (icon && icon.complete && icon.naturalWidth) {
      // the app's own icon on a light tile, as in the headset's picker
      fillBox(c, gx, gy, size, size * 0.62, 18, 'rgba(255,255,255,0.55)')
      const s = size * 0.46
      c.drawImage(icon, gx + (size - s) / 2, gy + (size * 0.62 - s) / 2, s, s)
    } else {
      fillBox(c, gx, gy, size, size * 0.62, 18, win.isDisplay ? '#2b2b2b' : colours[i % colours.length])
      c.fillStyle = '#fff'; c.font = `600 34px ${SANS}`; c.textAlign = 'center'
      c.fillText(win.isDisplay ? '🖥' : (win.app || '?').slice(0, 1).toUpperCase(), gx + size / 2, gy + size * 0.31)
    }
    c.textAlign = 'center'
    // the labels in dark ink: this picker sits on the room's light glass, not the headset's dark one
    c.fillStyle = '#1c2130'; c.font = `600 14px ${SANS}`
    c.fillText(clip(c, win.app || '?', size), gx + size / 2, gy + size * 0.62 + 18)
    c.fillStyle = 'rgba(28,33,48,0.65)'; c.font = `400 12px ${SANS}`
    c.fillText(clip(c, win.title || '', size), gx + size / 2, gy + size * 0.62 + 36)
    c.textAlign = 'left'
    if ((live.streaming || []).includes(win.id)) { c.fillStyle = '#2fbf7f'; c.beginPath(); c.arc(gx + size - 12, gy + 12, 6, 0, Math.PI * 2); c.fill() }   // streaming already
    live.macTiles.push({ x: gx, y: gy, w: size, h: size * 0.62 + 44, window: win })
  })
}
